import React, { useEffect, useRef, useState } from 'react'
import { useRetorikStore } from '../../Contexts/RetorikContext'
import { useViewStore } from '../../Contexts/ViewContext'
import { useSpeechStore } from '../../Contexts/SpeechContext'
import { useUtilsStore } from '../../Contexts/UtilsContext'
import { useDirectlineStore } from '../../Contexts/DirectlineContext'

import { CurrentSubView, Mode, RemoteButtonType, RecognitionState, Theme, CloseButtonCause, Routes } from '../../../models/enums'
import * as RemoteIcons from '../../Icons/RemoteIcons'
import { KeyboardIcon } from '../../Icons/MenuIcons'
import { CloseIcon } from '../../Icons/Miscellaneous'
import { useLocaleStore } from '../../Contexts/LocaleContext'

interface RemoteButtonProps {
  type: RemoteButtonType
  backgroundColor?: string
  cause?: CloseButtonCause
}

const RemoteButton = ({ type, backgroundColor, cause }: RemoteButtonProps): JSX.Element => {
  const translation = useLocaleStore((state) => state.currentTranslations)
  const theme = useViewStore((state) => state.theme)
  const setRoute = useViewStore((state) => state.actions.setRoute)
  const displaySubtitles = useRetorikStore((state) => state.displaySubtitles)
  const muted = useSpeechStore((state) => state.muted)
  const currentSubView = useUtilsStore((state) => state.currentSubView)
  const { setCurrentSubView, toggleDashboard, setDashboardOpened, setCloseDetailedPOI, setFullScreenImage, setCloseList } = useUtilsStore((state) => state.actions)
  const { toggleMuted, setActiveRecognitionState, setMode } = useSpeechStore((state) => state.actions)
  const { toggleSubtitles, setCanFocusSendBox } = useRetorikStore((state) => state.actions)
  const sendEvent = useDirectlineStore((state) => state.actions.sendEvent)

  const currentSubViewRef = useRef<CurrentSubView>(CurrentSubView.none)
  const [icon, setIcon] = useState<JSX.Element>()
  const [label, setLabel] = useState<string>()
  const [hover, setHover] = useState<boolean>(false)
  const [backgroundColorHover, setBackgroundColorHover] = useState<string | undefined>(undefined)

  useEffect(() => {
    currentSubViewRef.current = currentSubView
  }, [currentSubView])

  useEffect(() => {
    setBackgroundColorHover(backgroundColor?.replace('99', 'E6'))
  }, [backgroundColor])

  useEffect(() => {
    switch (type) {
      case RemoteButtonType.history:
        setLabel(translation.common.history)
        break
      case RemoteButtonType.home:
        setLabel('home')
        break
      case RemoteButtonType.language:
        setLabel(translation.common.lang)
        break
      case RemoteButtonType.menu:
        setLabel(translation.common.menu)
        break
      case RemoteButtonType.sound:
        setLabel(translation.common.sound)
        break
      case RemoteButtonType.subtitles:
        setLabel(translation.common.subtitle)
        break
      case RemoteButtonType.text:
        setLabel(translation.common.mode)
        break
      case RemoteButtonType.close:
        setLabel(translation.common.close)
        break
    }
  }, [type, translation])

  useEffect(() => {
    switch (type) {
      case RemoteButtonType.history:
        setIcon(<RemoteIcons.HistoryIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.home:
        setIcon(<RemoteIcons.HomeIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.language:
        setIcon(<RemoteIcons.LanguageIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.menu:
        setIcon(<RemoteIcons.MenuIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.sound:
        setIcon(muted ? <RemoteIcons.SoundOffIcon color={theme === Theme.dark ? '#FFF' : '#000'} /> : <RemoteIcons.SoundOnIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.subtitles:
        setIcon(displaySubtitles ? <RemoteIcons.SubtitlesOnIcon color={theme === Theme.dark ? '#FFF' : '#000'} /> : <RemoteIcons.SubtitlesOffIcon color={theme === Theme.dark ? '#FFF' : '#000'} />)
        break
      case RemoteButtonType.text:
        setIcon(<KeyboardIcon className='rf-w-5' color='#A4A7AF' />)
        break
      case RemoteButtonType.close:
        setIcon(<CloseIcon className='rf-h-8 rf-w-8' color='#000' strokeWidth={1} />)
        break
    }
  }, [type, theme, displaySubtitles, muted])

  const handleClick = (): void => {
    switch (type) {
      case RemoteButtonType.history:
        if (currentSubViewRef.current === CurrentSubView.history) {
          setCurrentSubView(CurrentSubView.none)
          sendEvent('Davi.CloseWindow', null, () => sendEvent('Davi.CloseWindowHistory', null))
        } else {
          setCurrentSubView(CurrentSubView.history)
        }
        break
      case RemoteButtonType.home:
        setCurrentSubView(CurrentSubView.none)
        sendEvent('showHome')
        break
      case RemoteButtonType.language:
        if (currentSubViewRef.current === CurrentSubView.languages) {
          setCurrentSubView(CurrentSubView.none)
          sendEvent('Davi.CloseWindow', null, () => sendEvent('Davi.CloseWindowLanguages', null))
        } else {
          setCurrentSubView(CurrentSubView.languages)
        }
        break
      case RemoteButtonType.menu:
        toggleDashboard()
        break
      case RemoteButtonType.sound:
        toggleMuted()
        break
      case RemoteButtonType.subtitles:
        toggleSubtitles()
        break
      case RemoteButtonType.text:
        setCanFocusSendBox(true)
        setActiveRecognitionState(RecognitionState.ForceClosing)
        setMode(Mode.text)
        break
      case RemoteButtonType.close: {
        switch (cause) {
          case CloseButtonCause.dashboard:
            setDashboardOpened(false)
            break
          case CloseButtonCause.detailedpoi:
            setCloseDetailedPOI(true)
            break
          case CloseButtonCause.fullscreenimage:
            setFullScreenImage(undefined)
            break
          case CloseButtonCause.list:
            setCloseList(Date.now())
            break
          case CloseButtonCause.emergency:
          case CloseButtonCause.history:
          case CloseButtonCause.languages:
          case CloseButtonCause.weather:
          case CloseButtonCause.map:
            setCurrentSubView(CurrentSubView.none)
            sendEvent('Davi.CloseWindow', null, () => sendEvent(`Davi.CloseWindow${cause}`, null))
            break
          case CloseButtonCause.news:
            setRoute(Routes.Home)
            break
          case CloseButtonCause.none:
          default:
            break
        }
      }
    }
  }

  return (
    <div
      className='rf-w-10 rf-h-10 rf-rounded-half rf-flex rf-justify-center rf-items-center rf-cursor-pointer'
      style={{
        zIndex: type === RemoteButtonType.close ? 60 : 5,
        backgroundColor: hover ? backgroundColorHover : backgroundColor,
        boxShadow: type === RemoteButtonType.text || type === RemoteButtonType.close ? '0px 0px 6px #1A1A1A80' : undefined
      }}
      aria-label={label}
      // data-map used in interactive map to use this button externaly
      data-map={type === RemoteButtonType.close ? 'fermer' : 'other'}
      onClick={handleClick}
      onPointerEnter={(): void => setHover(true)}
      onPointerLeave={(): void => setHover(false)}
    >
      {icon}
    </div>
  )
}

export default RemoteButton
